import { WHATSAPP_URL } from "@/constants";
import { DOCES } from "@/data/doces";
import { DoceCard } from "@/components/DoceCard";
import { WhatsAppIcon } from "@/components/icons";
import Seo from "@/components/Seo";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Bakery",
  name: "Doce Casa",
  slogan: "Um doce com cheirinho de casa",
  description:
    "Bolos, brigadeiros, pudins e sobremesas artesanais preparados com carinho. Encomendas na Amadora, Grande Lisboa e arredores.",
  areaServed: ["Amadora", "Grande Lisboa"],
};

export default function Home() {
  const scrollToDoces = (e) => {
    e.preventDefault();
    document.getElementById("doces")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Seo
        title="Doce Casa | Bolos e Sobremesas Artesanais na Amadora"
        siteName="Doce Casa"
        description="Bolos, brigadeiros, pudins e sobremesas artesanais preparados com carinho. Encomendas na Amadora, Grande Lisboa e arredores."
        image="/images/bolo-laranja.jpg"
        jsonLd={jsonLd}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 md:py-24 grid gap-12 lg:grid-cols-2 items-center" data-testid="hero-section">
        <div className="animate-rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-beige border border-sand px-4 py-1.5 text-xs font-medium tracking-widest uppercase text-caramel" data-testid="hero-badge">
            Pastelaria artesanal · Amadora
          </span>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-chocolate">
            Doce Casa
          </h1>
          <p className="mt-3 font-hand text-3xl sm:text-4xl text-terracotta" data-testid="hero-slogan">
            Um doce com cheirinho de casa.
          </p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-cocoa max-w-xl">
            Bolos e sobremesas feitos com carinho, para os dias de festa e para
            aqueles em que apetece um abraço em forma de doce.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href="#doces"
              onClick={scrollToDoces}
              data-testid="hero-sweets-btn"
              className="inline-flex items-center justify-center rounded-full bg-chocolate px-8 py-4 text-base font-medium text-cream shadow-md transition-all duration-200 hover:bg-chocolate-light hover:shadow-lg"
            >
              Conhecer os nossos doces
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-caramel px-8 py-4 text-base font-medium text-caramel transition-all duration-200 hover:bg-caramel hover:text-cream"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Pedir pelo WhatsApp
            </a>
          </div>
        </div>

        <div className="relative animate-rise" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -inset-3 rounded-[2.75rem] bg-beige rotate-2" aria-hidden="true" />
          <img
            src="/images/bolo-laranja.jpg"
            alt="Bolo de laranja artesanal com cobertura cremosa num prato de bolo branco, sobre uma mesa clara com ambiente caseiro"
            className="relative w-full aspect-square object-cover rounded-[2.5rem] shadow-[0_30px_60px_-25px_rgba(61,35,20,0.35)]"
            data-testid="hero-image"
          />
        </div>
      </section>

      <section id="doces" className="bg-beige/60 border-y border-sand scroll-mt-24" data-testid="sweets-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-chocolate">
              Os nossos doces
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-cocoa">
              Doces preparados com carinho para celebrar momentos especiais ou
              simplesmente tornar o teu dia mais aconchegante.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="sweets-grid">
            {DOCES.map((doce) => (
              <DoceCard key={doce.id} doce={doce} />
            ))}
          </div>

          <p className="mt-10 text-center text-xs sm:text-sm text-cocoa/80 italic" data-testid="sweets-disclaimer">
            Imagens ilustrativas. Sabores, decoração e disponibilidade sujeitos a confirmação.
          </p>
          <p className="mt-3 text-center text-sm font-medium text-chocolate" data-testid="sweets-delivery-area">
            Entregas na Amadora, Grande Lisboa e arredores.
          </p>

          <div className="mt-10 flex justify-center">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sweets-whatsapp-btn"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-caramel px-10 py-5 text-lg font-medium text-cream shadow-lg transition-all duration-200 hover:bg-caramel-dark hover:shadow-xl hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="w-6 h-6" />
              Pedir pelo WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
